import { Injectable } from '@angular/core';
import { HttpServiceParent } from './httpServiceParent';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { PhobosTaskInstance } from '../domain/phobos-task-instance';
import { FormTaskAssignment } from '../domain/form-task-assignment';
import { Observable } from 'rxjs';
import { WorkflowFile } from '../domain/workflow-file';
import { Comment } from '../domain/comment';
import { CommentRequest } from './request/comment-request';
import { FollowUp } from '../domain/follow-up';
import { ModelSummary } from '../domain/Ai/model-summary';
import { PropertyStoreService } from './stores/property-store.service';
import { AiProcessingTask } from '../domain/Ai/ai-processing-task';
import { PmsRowSupportingFileCount } from '../domain/pms-row-supporting-file-count';

@Injectable({
   providedIn: 'root'
})
export class PhobosTaskInstanceService extends HttpServiceParent {
   constructor(
      private http: HttpClient,
      private propertyStore: PropertyStoreService
   ) {
      super(propertyStore);
   }

   getTaskAi(taskInstanceId: number): Observable<ModelSummary> {
      return this.http.get<ModelSummary>(
         `${environment.apiEndpoint}/phobos/task/instance/${encodeURIComponent(
            taskInstanceId
         )}/ai`,
         {
            headers: this.headers,
            responseType: 'json'
         }
      );
   }

   claimAssignmentInstance(assignmentId: number) {
      return this.http.get<number>(
         `${
            environment.apiEndpoint
         }/phobos/assignment/${encodeURIComponent(assignmentId)}`,
         {
            headers: this.headers,
            responseType: 'json'
         }
      );
   }

   createNewPhobosTaskInstance(formTaskId: number, propId: number) {
      return this.http.get<PhobosTaskInstance>(
         `${environment.apiEndpoint}/phobos/task/${encodeURIComponent(
            formTaskId
         )}/instance/property/${encodeURIComponent(propId)}`,
         {
            headers: this.headers,
            responseType: 'json'
         }
      );
   }

   assignPhobosTaskInstance(
      formTaskInstanceId: number,
      groupId: number,
      propId: number
   ) {
      return this.http.post<number>(
         `${environment.apiEndpoint}/phobos/task/instance/${encodeURIComponent(
            formTaskInstanceId
         )}`,
         {
            groupId: groupId,
            propertyId: propId
         },
         {
            headers: this.headers,
            responseType: 'json'
         }
      );
   }

   getPhobosTaskInstance(formTaskInstanceId: number) {
      return this.http.get<PhobosTaskInstance>(
         `${environment.apiEndpoint}/phobos/task/instance/${encodeURIComponent(
            formTaskInstanceId
         )}`,
         {
            headers: this.headers,
            responseType: 'json'
         }
      );
   }

   getPhobosTaskInstanceAiData(formTaskInstanceId: number): Observable<ModelSummary> {
      return this.http.get<ModelSummary>(
         `${environment.apiEndpoint}/phobos/task/instance/${encodeURIComponent(
            formTaskInstanceId
         )}/ai`,
         {
            headers: this.headers,
            responseType: 'json'
         }
      );
   }

   getPhobosTaskInstanceAssignments(formTaskInstanceId: number) {
      return this.http.get<FormTaskAssignment[]>(
         `${environment.apiEndpoint}/phobos/task/instance/${encodeURIComponent(
            formTaskInstanceId
         )}/assignment`,
         {
            headers: this.headers,
            responseType: 'json'
         }
      );
   }

   completePhobosTaskInstance(
      formTaskInstanceId: number,
      sendEmail: boolean = false,
      emailAddresses: number[] = []
   ) {
      return this.http.post<number>(
         `${environment.apiEndpoint}/phobos/task/instance/${encodeURIComponent(
            formTaskInstanceId
         )}/complete`,
         {
            sendEmail: sendEmail,
            emailAddresses: emailAddresses
         },
         {
            headers: this.headers,
            responseType: 'json'
         }
      );
   }

   reOpenPhobosTaskInstance(formTaskInstanceId: number) {
      return this.http.post<number>(
         `${environment.apiEndpoint}/phobos/task/instance/${encodeURIComponent(formTaskInstanceId)}/revert`,
         {},
         {
            headers: this.headers,
            responseType: 'json'
         }
      );
   }

   sendCompletePhobosTaskInstance(
      formTaskInstanceId: number,
      sendEmail: boolean = false,
      emailAddresses: number[] = []
   ) {
      return this.http.post<number>(
         `${environment.apiEndpoint}/phobos/task/instance/${encodeURIComponent(
            formTaskInstanceId
         )}/complete/email`,
         {
            sendEmail: sendEmail,
            emailAddresses: emailAddresses
         },
         {
            headers: this.headers,
            responseType: 'json'
         }
      );
   }

   getSupportingFilesByPhobosTaskInstance(id: number): Observable<WorkflowFile[]> {
      return this.http.get<WorkflowFile[]>(
         `${environment.apiEndpoint}/phobos/task/instance/${encodeURIComponent(
            id
         )}/file/supporting`,
         {
            headers: this.headers,
            responseType: 'json'
         }
      );
   }

   getSupportingFileSourceByPhobosTaskInstance(
      id: number,
      supportingFileId: number
   ): Observable<WorkflowFile> {
      return this.http.get<WorkflowFile>(
         `${environment.apiEndpoint}/phobos/task/instance/${encodeURIComponent(
            id
         )}/file/supporting/${encodeURIComponent(supportingFileId)}/source`,
         {
            headers: this.headers,
            responseType: 'json'
         }
      );
   }

   uploadSupportingFile(processId: number, supportingFile: File) {
      const formData = new FormData();
      formData.append('supportingFile', supportingFile);
      return this.http.post<WorkflowFile[]>(
         `${environment.apiEndpoint}/phobos/task/instance/${encodeURIComponent(
            processId
         )}/file/supporting`,
         formData,
         {
            headers: this.headers,
            responseType: 'json'
         }
      );
   }

   getPmsRowSupportingFileCounts(
      taskInstanceId: number,
      pmsDataId: number
   ): Observable<PmsRowSupportingFileCount[]> {
      return this.http.get<PmsRowSupportingFileCount[]>(
         `${environment.apiEndpoint}/phobos/task/instance/${encodeURIComponent(
            taskInstanceId
         )}/file/supporting/pms/${encodeURIComponent(pmsDataId)}/row-counts`,
         {
            headers: this.headers,
            responseType: 'json'
         }
      );
   }

   getPmsRowSupportingFiles(
      taskInstanceId: number,
      pmsDataId: number,
      targetRowId: string
   ): Observable<WorkflowFile[]> {
      return this.http.get<WorkflowFile[]>(
         `${environment.apiEndpoint}/phobos/task/instance/${encodeURIComponent(
            taskInstanceId
         )}/file/supporting/pms/${encodeURIComponent(
            pmsDataId
         )}/row/${encodeURIComponent(targetRowId)}`,
         {
            headers: this.headers,
            responseType: 'json'
         }
      );
   }

   getPmsRowSupportingFileSource(
      taskInstanceId: number,
      pmsDataId: number,
      targetRowId: string,
      supportingFileId: number
   ): Observable<WorkflowFile> {
      return this.http.get<WorkflowFile>(
         `${environment.apiEndpoint}/phobos/task/instance/${encodeURIComponent(
            taskInstanceId
         )}/file/supporting/pms/${encodeURIComponent(
            pmsDataId
         )}/row/${encodeURIComponent(targetRowId)}/${encodeURIComponent(
            supportingFileId
         )}/source`,
         {
            headers: this.headers,
            responseType: 'json'
         }
      );
   }

   uploadPmsRowSupportingFile(
      taskInstanceId: number,
      pmsDataId: number,
      targetRowId: string,
      supportingFile: File
   ): Observable<WorkflowFile[]> {
      const formData = new FormData();
      formData.append('supportingFile', supportingFile);

      return this.http.post<WorkflowFile[]>(
         `${environment.apiEndpoint}/phobos/task/instance/${encodeURIComponent(
            taskInstanceId
         )}/file/supporting/pms/${encodeURIComponent(
            pmsDataId
         )}/row/${encodeURIComponent(targetRowId)}`,
         formData,
         {
            headers: this.headers,
            responseType: 'json'
         }
      );
   }

   getCommentsForPhobosTaskInstance(id: number) {
      return this.http.get<Comment[]>(
         `${environment.apiEndpoint}/phobos/task/instance/${encodeURIComponent(
            id
         )}/comment`,
         {
            headers: this.headers,
            responseType: 'json'
         }
      );
   }

   addCommentForPhobosTaskInstance(
      passedComment: CommentRequest,
      id: number
   ): Observable<Comment> {
      return this.http.post<Comment>(
         `${environment.apiEndpoint}/phobos/task/instance/${encodeURIComponent(
            id
         )}/comment`,
         passedComment,
         {
            headers: this.headers,
            responseType: 'json'
         }
      );
   }

   addFollowUp(id: number, passedFollowup: FollowUp): Observable<FollowUp> {
      return this.http.post<FollowUp>(
         `${environment.apiEndpoint}/phobos/task/instance/${encodeURIComponent(
            id
         )}/followup`,
         passedFollowup,
         {
            headers: this.headers,
            responseType: 'json'
         }
      );
   }

   completeFollowUps(followUpIds: number[]): Observable<number> {
      return this.http.post<number>(
         `${environment.apiEndpoint}/phobos/followup/complete`,
         followUpIds,
         {
            headers: this.headers,
            responseType: 'json'
         }
      );
   }

   readMention(mentionIds: number[]): Observable<number> {
      return this.http.post<number>(
         `${environment.apiEndpoint}/phobos/mention/read`,
         mentionIds,
         {
            headers: this.headers,
            responseType: 'json'
         }
      );
   }

   completeMention(mentionIds: number[]): Observable<number> {
      return this.http.post<number>(
         `${environment.apiEndpoint}/phobos/mention/complete`,
         mentionIds,
         {
            headers: this.headers,
            responseType: 'json'
         }
      );
   }

   uploadWorkflowFile(processId: number, workflowFile: File) {
      const formData = new FormData();
      formData.append('worlflowfile', workflowFile);
      return this.http.post<PhobosTaskInstance>(
         `${environment.apiEndpoint}/phobos/task/instance/${encodeURIComponent(
            processId
         )}/file`,
         formData,
         {
            headers: this.headers,
            responseType: 'json'
         }
      );
   }

   isAiTasksProcessing(
      propId: number,
      isPendingOnly: boolean = true
   ): Observable<AiProcessingTask[]> {
      return this.http.get<AiProcessingTask[]>(
         `${environment.apiEndpoint}/phobos/task/instance/property/${encodeURIComponent(propId)}/ai`,
         {
            params: { IsPendingOnly: String(isPendingOnly) },
            headers: this.headers,
            responseType: 'json'
         }
      );
   }
}
